<template>
  <AppLayout @cambio="changeLenguage">
    <component :is="viewComponentName" :project="project" />

    <template v-slot:actionbar>
      <div class="d-flex align-items-center w-100">
        <button
          v-if="projects.length > 1"
          @click="$router.push({ name: 'project-list' })"
          class="back-button"
        >
          <svg fill="none" stroke="currentColor" height="24" width="24" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            ></path>
          </svg>
        </button>

        <div class="d-flex flex-grow-1 align-items-center">
          <TenantLogo class="d-none d-md-block" />

          <div
            class="d-none d-md-block ml-2 mr-2 ml-md-4 mr-md-4"
            style="height: 60px; width: 2px; background-color: #e9e9e9;"
          ></div>

          <div class="d-flex">
            <Logo :image-url="projectLogoUrl" :description="project.name" />

            <div class="d-none d-md-block ml-3">
              <h3 class="title">{{ project.name }}</h3>
              <p class="subtitle">{{ $t('lang.select_camera.msg') }}</p>
            </div>
          </div>

          <b-form-group class="ml-2 ml-md-4 mb-0">
            <b-form-radio-group
              id="btn-radios-1"
              v-model="viewMode"
              :options="viewOptions"
              buttons
              button-variant="outline-primary"
              name="radios-btn-default"
            ></b-form-radio-group>
          </b-form-group>
        </div>

        <!-- User Actions -->
        <div class="d-flex align-items-center ml-auto">
          <!-- Botón de perfil mejorado -->
          <b-button
            variant="light"
            class="profile-button d-flex align-items-center mr-3"
            @click="$router.push({ name: 'profile-home', params: { project: project.id } })"
            v-b-tooltip.hover
            :title="$t('lang.mi_perfil.msg') || 'Mi perfil'"
          >
            <div class="profile-avatar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </svg>
            </div>
            <span class="d-none d-md-inline ml-2">{{
              $t('lang.mi_perfil.msg') || 'Mi perfil'
            }}</span>
          </b-button>

          <!-- Botón de cerrar sesión mejorado -->
          <b-button
            variant="outline-danger"
            class="logout-button d-flex align-items-center"
            @click="$store.dispatch('auth/logout')"
            v-b-tooltip.hover
            :title="$t('lang.cerrar.msg')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="mr-2">
              <path
                d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"
              />
            </svg>
            <span class="d-none d-md-inline">{{ $t('lang.cerrar.msg') }}</span>
          </b-button>
        </div>
      </div>
    </template>
  </AppLayout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import store from '@/store';
import CamerasDefaultView from '@/components/CamerasDefaultView.vue';
import CamerasMapView from '@/components/CamerasMapView.vue';
import CamerasReportView from '@/components/CamerasReportView.vue';
import Logo from '@/components/Logo.vue';
import TenantLogo from '@/components/TenantLogo.vue';

export default {
  name: 'ProjectHome',
  components: {
    CamerasDefaultView,
    CamerasMapView,
    CamerasReportView,
    Logo,
    TenantLogo,
  },
  props: {
    project: { type: Object, required: true },
    view: { type: String, default: 'default' },
  },
  data() {
    return {
      viewMode: this.view,
      viewOptions: [
        { text: '', value: 'default' },
        { text: '', value: 'map' },
        { text: '', value: 'report' },
      ],
    };
  },
  created() {
    this.viewOptions[0].text = this.$t('lang.camera.msg');
    this.viewOptions[1].text = this.$t('lang.map.msg');
    this.viewOptions[2].text = this.$t('lang.report.msg');
  },
  methods: {
    changeLenguage(event) {
      this.viewOptions[0].text = this.$t('lang.camera.msg');
      this.viewOptions[1].text = this.$t('lang.map.msg');
      this.viewOptions[2].text = this.$t('lang.report.msg');
    },
  },
  computed: {
    viewComponentName() {
      return `cameras-${this.viewMode}-view`;
    },
    ...mapState('project', ['projects']),
    ...mapGetters('project', ['projectLogoUrl']),
  },

  async beforeRouteEnter(to, from, next) {
    try {
      const project = await store.dispatch('project/fetchProject', to.params.id);
      document.title = `${project.name} - Timelapse México`;
      to.params.project = project;
      next();
    } catch (error) {
      if (error.response && error.response.status == 404) {
        next({ name: '404', params: { resource: 'proyecto' } });
      } else {
        next({ name: 'network-issue' });
      }
    }
  },
};
</script>

<style>
.back-button {
  background: none;
  border: none;
  color: #484848;
}
.back-button:focus {
  outline: none;
}

/* Profile and logout button improvements */
.profile-button {
  border-radius: 25px !important;
  padding: 8px 16px !important;
  transition: all 0.2s ease-in-out;
  border: 1px solid #e9ecef;
  background-color: #f8f9fa;
  color: #495057;
}

.profile-button:hover {
  background-color: #e9ecef !important;
  border-color: #dee2e6 !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.profile-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.logout-button {
  border-radius: 25px !important;
  padding: 8px 16px !important;
  transition: all 0.2s ease-in-out;
}

.logout-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .profile-button,
  .logout-button {
    padding: 8px 12px !important;
  }
}
</style>
